<template>
  <div class="d-flex flex-column mt-4">
    <div class="grid-header">
      <h1>{{ $t("inmueble.ui.facturas") }}</h1>
      <div class="toolbar">
        <button type="button" @click.prevent="anadirFactura()" class="btn btn-link">
          <i class="fa-solid fa-plus"></i>
        </button>
      </div>
    </div>
    <DxDataGrid v-bind="{ ...getDefaultGridProperties(), paging: { pageSize: 5 } }" :data-source="gridSource"
      ref="grid">
      <DxColumnFixing :enabled="true" />

      <DxColumn data-field="Fecha" :caption="$t('inmueble.factura.fecha')" data-type="date" format="dd/MM/yyyy">
      </DxColumn>
      <DxColumn data-field="Tipo" :caption="$t('inmueble.factura.tipo')" cell-template="tipoTemplate">
        <DxLookup v-bind="{ ...getLookup('tipoFacturaInmueble', 'values') }"></DxLookup>
      </DxColumn>
      <DxColumn data-field="Importe" :caption="$t('inmueble.factura.importe')" data-type="number">
        <DxFormat v-bind="currencyFormat()"></DxFormat>
      </DxColumn>
      <DxColumn type="buttons" :caption="$t('inmueble.factura.ui.acciones')" :fixed="true"
        fixed-position="right" width="200px" :buttons="[
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-eye text-success',
            hint: $t('inmueble.factura.ui.ver'),
            onClick: (e: any) => ver(e.row.data.Ruta)
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-regular fa-trash text-success',
            hint: $t('inmueble.factura.ui.borrar'),
            onClick: (e: any) => borrar(e.row.data.Id)
          }
        ]"/>

      <template #tipoTemplate="{ data }">
        
          <div :class="[`d-inline-block rounded py-1 px-2 me-2`, `tipologia ${data.value}`]">
            <i :class="['me-1',
              data.value === 'AGUA' ? 'fa-regular fa-droplet' :
                data.value === 'LUZ' ? 'fa-regular fa-lightbulb' :
                  'fa-regular fa-fire'
            ]"></i>
            {{ $t(`tipoFacturaInmueble.values.${data.value}`) }}
          </div>
        
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { DxDataGrid, DxColumnFixing, DxColumn, DxLookup, DxFormat } from 'devextreme-vue/data-grid';
import { useApi } from '@api/integration';
import { useIntl } from '@/.shared/services/i18n';
import { getDefaultGridProperties } from '@helpers/grid-helper';
import type CustomStore from 'devextreme/data/custom_store';
import type { DXLoadOptions } from '@/.shared/.api';
import { getLookup, currencyFormat } from '@helpers/grid-helper';
import DocumentoVisor from '@/.shared/components/DocumentViewer.vue';
import { useModalService } from '@/.shared/services/modal-service';
import { messageBoxResult } from '@/.shared/components/dialogs/message-box';
import { toastError, toastSuccess } from '@/.shared/services/notification-service';
import { AddFacturaInmueble } from '@/components/inmuebles/models/AddFacturaInmueble';
import AnadirFacturaForm from '@/components/inmuebles/AnadirFacturaForm.vue';

const api = useApi();
const intl = useIntl();

const grid = ref<DxDataGrid>();
const gridSource = ref<CustomStore>();
const modal = useModalService();

const props = defineProps({
  inmuebleId: {
    type: String,
    required: true
  }
});

onMounted(async () => {
  await setDataSource();
});

const setDataSource = async () => {
  gridSource.value = api.apiStore({
    key: "Id",
    load: async (p) => {
      const q = p as DXLoadOptions;
      return await api.callStandardApi("getFacturasInmuebleForGrid", { showLoading: false }, props.inmuebleId, q)
    }
  });
}

const anadirFactura = () => {
  let title = `${intl.i18n.t("inmueble.factura.ui.titulo_alta")}`;
  modal.showModal(
    AnadirFacturaForm,
    { model: new AddFacturaInmueble(), inmuebleId: props.inmuebleId },
    { title: title, width: 600, height: 400 }
  ).then((value) => {
    if (value == messageBoxResult.ok)
      grid.value?.instance?.refresh();
  });
}

const ver = async (ruta: string) => {
  var tempUrl = await api.callStandardApi("documentosGenerateTempUrl", { showLoading: false }, ruta);
  var file = ruta.split('/').pop();
  let title = `${file}`
  await modal.showModal(DocumentoVisor, { ruta: tempUrl }, { title: title });
}

const borrar = async (id: string) => {
  let respuesta = await modal.messageBox(intl.i18n.t('inmueble.factura.ui.borrar_pregunta'),
    intl.i18n.t('inmueble.factura.ui.titulo_confirma_borrar'), "okCancel", "warning");
  if (respuesta == messageBoxResult.ok)
    await api.callStandardApi("deleteFacturaInmueble", { showLoading: true }, props.inmuebleId, id)
      .then(() => {
        toastSuccess(intl.i18n.t('inmueble.factura.ui.borrado_exito'));
        grid.value?.instance?.refresh();
      })
      .catch(() => toastError(intl.i18n.t('inmueble.factura.ui.borrado_fallo')));
}

</script>


<style lang="scss" scoped>


</style>